// Keeps a quick-buy card's selected variant, image, price and button in sync with its swatches.
// Hovering a swatch (mouse/trackpad) previews that color's photo; leaving the swatches restores the selected one.
class SQuickBuy extends HTMLElement {
  #ac;
  #preloaded = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      this.#select(input);
    }, { signal });

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.addEventListener('pointerover', (event) => {
      if (!canHover.matches || event.pointerType === 'touch') return;
      this.#preload();
      const label = event.target instanceof Element ? event.target.closest('label') : null;
      const input = label?.querySelector('input[type="radio"]');
      if (input && this.contains(input)) this.#showImage(input);
    }, { signal });
    this.addEventListener('pointerout', (event) => {
      if (!canHover.matches || event.pointerType === 'touch') return;
      const group = event.target instanceof Element ? event.target.closest('fieldset') : null;
      if (!group || (event.relatedTarget instanceof Node && group.contains(event.relatedTarget))) return;
      const checked = this.querySelector('input[type="radio"]:checked');
      if (checked) this.#showImage(checked);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // Warm the browser cache with every color's photo so hover previews appear without a blank flash.
  #preload() {
    if (this.#preloaded) return;
    this.#preloaded = true;
    const image = this.querySelector('[data-quick-buy-image]');
    this.querySelectorAll('input[type="radio"][data-image]').forEach((input) => {
      const img = new Image();
      if (input.dataset.srcset) {
        img.sizes = image?.sizes || '50vw';
        img.srcset = input.dataset.srcset;
      }
      img.src = input.dataset.image;
    });
  }

  #showImage(input) {
    const image = this.querySelector('[data-quick-buy-image]');
    if (!image || !input.dataset.image || image.dataset.showing === input.value) return;
    image.dataset.showing = input.value;
    const swap = () => {
      if (image.dataset.showing !== input.value) return;
      // Lifestyle photos fill the tile ("cover"); cut-out product photos sit padded ("contain").
      const fitClass = input.dataset.fit === 'cover' ? image.dataset.coverClass : image.dataset.containClass;
      if (fitClass && image.className !== fitClass) image.className = fitClass;
      image.style.removeProperty('object-position');
      image.srcset = input.dataset.srcset || '';
      image.src = input.dataset.image;
    };
    image.getAnimations?.().forEach((animation) => animation.cancel());
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !image.animate) {
      swap();
    } else {
      image.animate([{ opacity: 1 }, { opacity: 0.2 }], { duration: 120, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }).finished.then(() => {
        swap();
        image.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 220, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      }, () => {});
    }
  }

  #select(input) {
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = input.value;

    const label = this.querySelector('[data-quick-buy-label]');
    if (label) label.textContent = input.dataset.label || '';

    const price = this.querySelector('[data-quick-buy-price] bdi');
    if (price && input.dataset.price) price.textContent = input.dataset.price;

    this.#showImage(input);

    const button = this.querySelector('[data-quick-buy-button]');
    if (button) {
      const available = input.dataset.available === 'true';
      if (available) {
        button.removeAttribute('aria-disabled');
        button.textContent = this.dataset.addLabel || button.textContent;
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.textContent = this.dataset.soldOutLabel || button.textContent;
      }
    }
  }
}

if (!customElements.get('s-quick-buy')) {
  customElements.define('s-quick-buy', SQuickBuy);
}

// Side-by-side quick-buy tiles get matching text panels: the shorter panel grows to the tallest one in its row.
const matchPanelHeights = () => {
  const panels = [...document.querySelectorAll('[data-quick-buy-panel]')];
  panels.forEach((panel) => { panel.style.minHeight = ''; });
  const rows = [];
  panels.forEach((panel) => {
    const top = panel.closest('s-quick-buy')?.getBoundingClientRect().top ?? panel.getBoundingClientRect().top;
    const row = rows.find((entry) => Math.abs(entry.top - top) < 8);
    if (row) row.panels.push(panel);
    else rows.push({ top, panels: [panel] });
  });
  rows.forEach(({ panels: row }) => {
    if (row.length < 2) return;
    const tallest = Math.max(...row.map((panel) => panel.getBoundingClientRect().height));
    row.forEach((panel) => { panel.style.minHeight = `${Math.ceil(tallest)}px`; });
  });
};

let panelFrame = 0;
let lastWidth = 0;
const queuePanelMatch = () => {
  cancelAnimationFrame(panelFrame);
  panelFrame = requestAnimationFrame(matchPanelHeights);
};

if ('ResizeObserver' in window) {
  new ResizeObserver(() => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    queuePanelMatch();
  }).observe(document.documentElement);
}
window.addEventListener('load', queuePanelMatch);
document.addEventListener('shopify:section:load', queuePanelMatch);
document.addEventListener('shopify:block:select', queuePanelMatch);
document.fonts?.ready.then(queuePanelMatch);
customElements.whenDefined('s-quick-buy').then(queuePanelMatch);
queuePanelMatch();
